<template>
    <!-- Табель показывается только для загруженных подразделения/периода (все месяцы недели):
         иначе загрузка или ошибка, не прежние часы и не «ВЫХ». -->
    <div v-if="isLoading" class="flex justify-center h-[70vh] items-center">
        <Loader />
    </div>
    <div v-else-if="!isReady" class="ds-week-table-message">
        Не удалось загрузить табель за {{ layout === 'week' ? 'выбранную неделю' : 'выбранный месяц' }}. Повторите выбор периода.
    </div>
    <div v-else-if="!filteredSchedules.length" class="ds-week-table-message">
        Ничего не найдено
    </div>
    <div v-else v-for="row in rows" :key="row.schedule.id" class="ds-week-row">
        <div class="ds-week-lead-cell">
            <!-- Клик сотрудника — карточка, как в графике (Schedule.openUserPanel). -->
            <Employee :schedule="row.schedule" layout="week" :position="row.schedule.position?.name" class="cursor-pointer"
                @click="emit('user-clicked', row.schedule)"
                :warned="isWarnedEmployee(row.schedule.id)" :hours-limit-violation="getHoursLimitViolation(row.schedule.id)">
                <template #metrics>
                    <template v-if="store.isEmployeeStatsVisible">
                        <Tooltip v-if="row.totals.normText" :text="row.totals.normText" placement="bottom">
                            <template #trigger>
                                <span class="ds-timesheet-total-main" :class="{ 'ds-timesheet-total-main-exceeded': row.totals.exceeded }">{{ row.totals.main }}</span>
                            </template>
                        </Tooltip>
                        <span v-else class="ds-timesheet-total-main">{{ row.totals.main }}</span>
                        <span class="ds-timesheet-total-additional">{{ row.totals.additional }}</span>
                        <span class="ds-timesheet-total-fact">{{ row.totals.fact }}</span>
                    </template>
                </template>
            </Employee>
            <!-- Превышение нормы — оранжевая полоса сотрудника, как у предупреждений графика. -->
            <div v-if="row.totals.exceeded" class="ds-timesheet-norm-stripe" aria-hidden="true" />
        </div>
        <div class="ds-week-days">
            <template v-if="layout === 'month'">
                <TimeSheetItem v-for="date in dates" :key="date" :schedule="row.schedule" :date
                    @click="onDayClick($event, date, row.schedule)" />
            </template>
            <template v-else>
                <TimeSheetWeekItem v-for="date in dates" :key="date" :schedule="row.schedule" :date
                    @click="onDayClick($event, date, row.schedule)" />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Schedule } from '@/types'
import { computed, onMounted } from 'vue'
import dayjs from 'dayjs'
import TimeSheetItem from './TimeSheetItem.vue'
import TimeSheetWeekItem from './TimeSheetWeekItem.vue'
import Loader from '@/ui/Loader.vue'
import Tooltip from '@/components/Tooltip.vue'
import Employee from '@/components/employee/Employee.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { formatTimesheetSeconds, type TimesheetTotals } from './timesheetCell'

interface Props {
    schedules: Schedule[]
    // Точные даты столбцов: все дни месяца или Пн–Вс недели (могут относиться к двум месяцам).
    dates: string[]
    layout: 'month' | 'week'
}

const store = useScheduleStore()
const departmentStore = useDepartmentStore()
const props = defineProps<Props>()
const emit = defineEmits(['day-clicked', 'user-clicked'])

// Аутсорс и вакансии в табель не попадают (прежнее правило).
const filteredSchedules = computed(() => props.schedules.filter(item => !item.is_outsource && !item.is_vacancy))

// Строки и смены графика (нарушения, кадровые документы) тоже должны относиться к текущему контексту.
const isReady = computed(() => store.isScheduleContextLoaded && store.isTimesheetViewLoaded)
const isLoading = computed(() => store.loading || (!store.isTimesheetViewLoaded && store.isTimesheetViewLoading))

const isWarnedEmployee = (id: string) => departmentStore.warnedEmployee.includes(id)
const getHoursLimitViolation = (id: string) => departmentStore.hoursLimitViolations.find(item => item.employment_id === id)

// Норма — hours_norm.values[].value (часы) из показателей сотрудника за тот же месяц; нет значения — нормы нет, не ноль.
const normByEmployment = computed(() => {
    const norms = new Map<string, number>()
    if (!store.isStatsContextLoaded) return norms
    for (const item of store.stats?.hours_norm?.values ?? []) {
        const value = Number(item?.value)
        if (item?.employment_id && item.value !== null && item.value !== '' && Number.isFinite(value)) norms.set(item.employment_id, value)
    }
    return norms
})

// Итоги — monthly_totals месяца выбранного периода (в неделе — месяца воскресенья), без пересчёта по дням.
function monthTotals(schedule: Schedule) {
    const month = store.currentScheduleDate.format('YYYY-MM')
    const totals: TimesheetTotals = store.getEmploymentTimesheet(schedule.id, month)?.timesheet?.monthly_totals ?? {}
    const norm = normByEmployment.value.get(schedule.id)
    const main = totals.main_seconds
    const hasMain = typeof main === 'number' && Number.isFinite(main)
    const exceeded = norm !== undefined && hasMain && main > norm * 3600
    let normText = ''
    if (norm !== undefined && hasMain) {
        normText = exceeded
            ? `Основные часы превышают норму ${norm} ч на ${formatTimesheetSeconds(main - norm * 3600)}`
            : `Норма ${norm} ч`
    }
    return {
        main: formatTimesheetSeconds(totals.main_seconds),
        additional: formatTimesheetSeconds(totals.additional_seconds),
        fact: formatTimesheetSeconds(totals.fact_seconds),
        exceeded,
        normText,
    }
}

const rows = computed(() => filteredSchedules.value.map(schedule => ({ schedule, totals: monthTotals(schedule) })))

// Клик дня — прежняя панель исходной смены с точной датой; право проверяет Schedule.vue (can_edit_timesheet).
function onDayClick(event: MouseEvent, date: string, schedule: Schedule) {
    emit('day-clicked', { day: dayjs(date).date(), date, index: store.schedules.indexOf(schedule), cell: event.currentTarget })
}

onMounted(() => {
    store.fetchTimesheetView()
})
</script>
